import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Search, SearchX, Package } from "lucide-react";
import { useReserve } from "../context/ReserveContext";
import { Reveal, ChapterTag } from "./Reveal";
import { Gallery } from "./Gallery";
import { API, fmt, pct, savings } from "../lib/store";

const slugify = (c) =>
    ({ "Wall Ovens": "ovens", "Gas Ranges": "gas-ranges" }[c] ||
        c.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));

const selectCls =
    "styled-select cursor-pointer rounded-lg border border-white/15 bg-ink2 py-2.5 pl-4 pr-9 font-mono text-[11px] uppercase tracking-widest text-slate-200 focus:border-gold focus:outline-none transition-colors";

function ProductCard({ p, index }) {
    const { open } = useReserve();
    const reserveBtn = (
        <button
            data-testid={`product-card-reserve-button-${index + 1}`}
            onClick={() => open(p)}
            className="rounded-full border border-gold/60 px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-widest text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
        >
            Reserve
        </button>
    );
    return (
        <motion.article
            layout
            data-testid={`product-card-${index + 1}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="group relative overflow-hidden rounded-2xl border border-white/10 bg-ink2 transition-colors duration-300 hover:border-gold/50"
        >
            <div className="relative aspect-square overflow-hidden">
                <Gallery images={p.images} alt={p.name} index={index} />
                <span className="absolute left-4 top-4 z-10 rounded-full border border-white/15 bg-ink/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white backdrop-blur-md">
                    {p.brand}
                </span>
                {p.our_price && p.hd_price ? (
                    <span
                        data-testid={`product-card-savings-tag-${index + 1}`}
                        className="absolute right-4 top-4 z-10 rounded-full bg-save px-3 py-1.5 font-mono text-[11px] font-bold text-ink"
                    >
                        {pct(p)}% OFF RETAIL
                    </span>
                ) : null}
            </div>
            <div className="p-5 md:p-6">
                <h3 className="font-display font-bold text-lg md:text-xl leading-snug">{p.name}</h3>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    {p.model && <span>Model {p.model}</span>}
                    {p.model && <span className="text-slate-600">·</span>}
                    <span className={p.stock_qty > 0 ? "" : "text-hdred"}>
                        {p.stock_qty > 0 ? `${p.stock_qty} in stock` : "Out of stock"}
                    </span>
                    <span className="text-slate-600">·</span>
                    <span>{p.category}</span>
                </p>
                {p.our_price ? (
                    <div className="mt-5 flex items-end justify-between gap-3">
                        <div>
                            <p
                                data-testid={`product-card-price-homedepot-${index + 1}`}
                                className="font-mono text-xs text-hdred line-through decoration-2"
                            >
                                RETAIL {fmt(p.hd_price)}
                            </p>
                            <p
                                data-testid={`product-card-price-our-${index + 1}`}
                                className="mt-1 font-display text-3xl md:text-4xl font-extrabold leading-none text-gold"
                            >
                                {fmt(p.our_price)}
                            </p>
                            <p className="mt-1.5 font-mono text-[11px] font-bold text-save">
                                YOU SAVE {fmt(savings(p))}
                            </p>
                        </div>
                        {reserveBtn}
                    </div>
                ) : (
                    <div className="mt-5 flex items-end justify-between gap-3">
                        <div>
                            <p
                                data-testid={`product-card-price-our-${index + 1}`}
                                className="font-display text-2xl md:text-3xl font-extrabold leading-none text-gold"
                            >
                                CALL FOR PRICE
                            </p>
                            <p className="mt-1.5 font-mono text-[11px] text-slate-400">
                                Always ~40%+ under retail
                            </p>
                        </div>
                        {reserveBtn}
                    </div>
                )}
            </div>
        </motion.article>
    );
}

export default function Inventory() {
    const [products, setProducts] = useState([]);
    const [meta, setMeta] = useState(null);
    const [cat, setCat] = useState("All");
    const [brand, setBrand] = useState("All Brands");
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("savings");
    const [inStockOnly, setInStockOnly] = useState(false);

    useEffect(() => {
        fetch(`${API}/products`).then((r) => r.json()).then(setProducts).catch(() => {});
        fetch(`${API}/meta`).then((r) => r.json()).then(setMeta).catch(() => {});
    }, []);

    const ACCESSORY_CAT = "Accessories";

    const filtered = useMemo(() => {
        let list = products.filter((p) => {
            if (brand !== "All Brands" && p.brand !== brand) return false;
            if (inStockOnly && p.stock_qty <= 0) return false;
            const s = search.trim().toLowerCase();
            if (s && !`${p.name} ${p.brand} ${p.category} ${p.model}`.toLowerCase().includes(s)) return false;
            return true;
        });
        if (sort === "savings") list.sort((a, b) => (b.savings_amount || 0) - (a.savings_amount || 0));
        if (sort === "price-asc") list.sort((a, b) => a.our_price - b.our_price);
        if (sort === "price-desc") list.sort((a, b) => b.our_price - a.our_price);
        return list;
    }, [products, brand, search, sort, inStockOnly]);

    // main appliances (accessories pulled out into their own section)
    const mainList = useMemo(
        () => filtered.filter((p) => p.category !== ACCESSORY_CAT && (cat === "All" || p.category === cat)),
        [filtered, cat]
    );
    const accessories = useMemo(() => filtered.filter((p) => p.category === ACCESSORY_CAT), [filtered]);
    const filterCategories = useMemo(() => (meta?.categories || []).filter((c) => c !== "Accessories"), [meta]);
    const showAccessories = cat === "All" && accessories.length > 0;
    const shownCount = mainList.length + (showAccessories ? accessories.length : 0);

    // group main appliances by brand, then by category within each brand
    const groups = useMemo(() => {
        const byBrand = new Map();
        mainList.forEach((p) => {
            const b = byBrand.get(p.brand) || new Map();
            const list = b.get(p.category) || [];
            list.push(p);
            b.set(p.category, list);
            byBrand.set(p.brand, b);
        });
        return [...byBrand.entries()].map(([b, cats]) => [b, [...cats.entries()]]);
    }, [mainList]);

    let running = 0;

    return (
        <section id="inventory" className="relative py-24 md:py-32 bg-ink2/30 border-y border-white/10">
            <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                <ChapterTag num="03" title="The Inventory Vault" testid="inventory-chapter-tag" />
                <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
                    <Reveal delay={0.05}>
                        <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-5xl leading-[1.02]">
                            On the floor <span className="text-gold">right now.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p data-testid="inventory-count" className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">
                            {shownCount} of {products.length} appliances · {products.filter((p) => p.stock_qty > 0).length} in stock
                        </p>
                    </Reveal>
                </div>

                {/* filter bar */}
                <Reveal delay={0.15}>
                    <div className="mt-10 flex flex-col gap-5 rounded-2xl border border-white/10 bg-ink/60 backdrop-blur-xl p-5">
                        <div className="flex flex-wrap gap-2">
                            <button
                                data-testid="category-filter-all"
                                onClick={() => setCat("All")}
                                className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-all duration-300 ${
                                    cat === "All"
                                        ? "border-gold bg-gold text-ink font-bold"
                                        : "border-white/15 text-slate-300 hover:border-gold/60 hover:text-gold"
                                }`}
                            >
                                All
                            </button>
                            {filterCategories.map((c) => (
                                <button
                                    key={c}
                                    data-testid={`category-filter-${slugify(c)}`}
                                    onClick={() => setCat(c)}
                                    className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-all duration-300 ${
                                        cat === c
                                            ? "border-gold bg-gold text-ink font-bold"
                                            : "border-white/15 text-slate-300 hover:border-gold/60 hover:text-gold"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                data-testid="filter-instock-only"
                                onClick={() => setInStockOnly((v) => !v)}
                                className={`flex items-center gap-2 rounded-full border px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-all duration-300 ${
                                    inStockOnly
                                        ? "border-save bg-save text-ink font-bold"
                                        : "border-white/15 text-slate-300 hover:border-save/60 hover:text-save"
                                }`}
                            >
                                <Package className="h-3.5 w-3.5" />
                                In stock only
                            </button>
                            <div className="relative flex-1 min-w-[220px]">
                                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                                <input
                                    data-testid="search-input-field"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search model or keyword…"
                                    className="w-full rounded-lg border border-white/15 bg-ink2 py-2.5 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none transition-colors"
                                />
                            </div>
                            <div className="relative">
                                <select
                                    data-testid="brand-filter-select"
                                    value={brand}
                                    onChange={(e) => setBrand(e.target.value)}
                                    className={selectCls}
                                    aria-label="Filter by brand"
                                >
                                    <option>All Brands</option>
                                    {(meta?.brands || []).map((b) => (
                                        <option key={b}>{b}</option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gold" />
                            </div>
                            <div className="relative">
                                <select
                                    data-testid="sort-select"
                                    value={sort}
                                    onChange={(e) => setSort(e.target.value)}
                                    className={selectCls}
                                    aria-label="Sort products"
                                >
                                    <option value="savings">Biggest Savings</option>
                                    <option value="price-asc">Price: Low → High</option>
                                    <option value="price-desc">Price: High → Low</option>
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gold" />
                            </div>
                        </div>
                    </div>
                </Reveal>

                {/* grouped by brand → category */}
                <AnimatePresence mode="popLayout">
                    {groups.map(([b, cats]) => (
                        <motion.div
                            layout
                            key={b}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.4 }}
                            className="mt-14 first:mt-12"
                        >
                            <div className="flex items-baseline justify-between border-b-2 border-gold/60 pb-3">
                                <h3 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">{b}</h3>
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                                    Brand
                                </span>
                            </div>
                            {cats.map(([c, list]) => (
                                <div key={c} className="mt-8">
                                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold mb-4">
                                        {c} <span className="text-slate-600">— {list.length}</span>
                                    </p>
                                    <motion.div layout className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                        <AnimatePresence mode="popLayout">
                                            {list.map((p) => {
                                                const i = running++;
                                                return <ProductCard key={p.id} p={p} index={i} />;
                                            })}
                                        </AnimatePresence>
                                    </motion.div>
                                </div>
                            ))}
                        </motion.div>
                    ))}
                </AnimatePresence>

                {/* accessories & parts — their own section */}
                {showAccessories && (
                    <div className="mt-16" data-testid="accessories-section">
                        <div className="flex items-baseline justify-between border-b-2 border-gold/60 pb-3">
                            <h3 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
                                Accessories <span className="text-gold">&amp; Parts</span>
                            </h3>
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                                Add-ons
                            </span>
                        </div>
                        <p className="mt-6 mb-4 max-w-xl font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                            Filters · Kits · Nozzles · Replacement parts
                            <span className="text-slate-600"> — {accessories.length}</span>
                        </p>
                        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            <AnimatePresence mode="popLayout">
                                {accessories.map((p) => {
                                    const i = running++;
                                    return <ProductCard key={p.id} p={p} index={i} />;
                                })}
                            </AnimatePresence>
                        </motion.div>
                    </div>
                )}

                {shownCount === 0 && (
                    <div data-testid="inventory-empty-state" className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-white/15 py-20 text-center">
                        <SearchX className="h-8 w-8 text-gold" />
                        <p className="font-display text-xl font-bold">Nothing matches that combo.</p>
                        <p className="max-w-sm text-sm text-slate-400">
                            Try clearing the search or switching back to All — or call the store, we get new stock weekly.
                        </p>
                    </div>
                )}

                <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                    * Retail reference prices shown per our ~40%-under-retail promise. Live stock counts from the store's dealer system.
                </p>
            </div>
        </section>
    );
}
